import Avatar from '@sdk/components/avatar/Avatar';

import {
  DeleteButton,
  Description,
  InnerWrapper,
  Meta,
  MetaAuthorName,
  MetaAvatar,
  MetaDate,
  MetaRecipients,
  MetaRecipientsTitle,
  MetaSeenBy,
  RecipientsWrapper,
  ScrollContainer,
  Title,
} from '@web/announcements/AnnouncementInnerContent/announcementInnerContent.styles';
import { Announcement } from '@web/announcements/types/Announcement';
import TrashSvg from '@web/assets/svg/UI/trash-alt.svg';
import EyeSvg from '@web/assets/svg/UI/eye.svg';
import { AttachmentsPreview } from '@web/components/AttachmentsPreview/AttachmentsPreview';
import { MarkdownDisplayWrapper } from '@web/components/MarkdownDisplayWrapper';
import { useAnnouncementInnerContent } from '@web/announcements/AnnouncementInnerContent/announcementInnerContent.hooks';
import { AnnouncementRecipients } from '@web/announcements/AnnouncementRecipients/AnnouncementRecipients';
import { useScrollbarGutterFallback } from '@web/common/hooks/useScrollbarGutterFallback';
import { useAnnouncementsUtils } from '@web/announcements/hooks/useAnnouncementUtils';

type Props = {
  announcement: Announcement;
  disableManagement?: boolean;
  onDelete?: (id: number) => void;
};

export const AnnouncementInnerContent = ({ disableManagement, onDelete, announcement }: Props) => {
  const { attachments, attachmentsCount, author, title, id, recipients } = announcement;
  const { parsedContent } = useAnnouncementInnerContent(announcement);
  const { checkIfCanManage, dateFormatter } = useAnnouncementsUtils();
  const { setRef, shouldApplyFallback } = useScrollbarGutterFallback();

  const canManage = !disableManagement && checkIfCanManage(announcement.author.id);
  const postedAt = dateFormatter(announcement.postedAt);

  return (
    <InnerWrapper>
      {canManage && onDelete && (
        <DeleteButton onClick={() => onDelete(id)}>
          <TrashSvg />
        </DeleteButton>
      )}

      <Meta>
        <MetaAvatar>
          <Avatar user={author} />
        </MetaAvatar>
        <MetaAuthorName>{author.fullname}</MetaAuthorName>
        <MetaDate>{postedAt}</MetaDate>
        {canManage && (
          <MetaRecipients>
            <MetaRecipientsTitle>Recipients</MetaRecipientsTitle>
            <MetaSeenBy>
              <EyeSvg />
              {recipients.read}/{recipients.total}
            </MetaSeenBy>
          </MetaRecipients>
        )}
      </Meta>

      {canManage && (
        <RecipientsWrapper>
          <AnnouncementRecipients total={recipients.total} ids={recipients.allIds} />
        </RecipientsWrapper>
      )}

      <ScrollContainer ref={setRef} shouldApplyFallback={shouldApplyFallback}>
        <Title>{title}</Title>
        <Description>
          <MarkdownDisplayWrapper as="div" dangerouslySetInnerHTML={parsedContent} />
        </Description>

        {!!attachmentsCount && <AttachmentsPreview attachments={attachments} />}
      </ScrollContainer>
    </InnerWrapper>
  );
};
